// useSecurityStore.ts
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { type RootState, type AppDispatch } from "./index";
import { StoreStatus } from "./StoreStatus";
import { initialStoreStateBase, StoreStateBase } from "./StoreStateBase";

import type { LoginRequest } from "../domain/SecuritySlice/LoginRequest";
import type { LoginResult } from "../domain/SecuritySlice/LoginResult";
import type { PasswordReset } from "../domain/SecuritySlice/PasswordReset";
import type { PasswordResetRequest } from "../domain/SecuritySlice/PasswordResetRequest";
import type { RegisterClient } from "../domain/SecuritySlice/RegisterClient";
import type { Token } from "../domain/SecuritySlice/Token";

import { AuthRepository } from "../infrastructure/SecuritySlice/AuthRepository";
import { RolesRepository } from "../infrastructure/SecuritySlice/RolesRepository";
import { getMinutesUntil } from "../application/Common/TimeUtility";
import globalRoles from "../globalRoles";

// ==== types ====
export interface UserSessionData {
  userFullName: string | undefined;
}

export interface SecurityState extends StoreStateBase {
  isLoggedIn: boolean;
  roles: string[];
  token: Token | undefined;
  userSession: UserSessionData | undefined;
}

// ==== action names ====
export const securityLogin = "security/login" as const;
export const securityLogout = "security/logout" as const;
export const securityRefreshToken = "security/refreshToken" as const;
export const securityFetchRoles = "security/fetchRoles" as const;
export const securityRegisterClient = "security/registerClient" as const;
export const securityRequestPasswordReset =
  "security/requestPasswordReset" as const;
export const securityPasswordReset = "security/passwordReset" as const;

// ==== initial state ====
const initialSecurityState: SecurityState = {
  ...initialStoreStateBase,
  isLoggedIn: false,
  roles: [],
  token: undefined,
  userSession: undefined,
};

// ==== selectors ====
export const selectSecurity = (s: RootState) => s.security;
export const selectIsLoggedIn = (s: RootState) => s.security.isLoggedIn;
export const selectIsLoggedInClient = (s: RootState) => s.security.isLoggedIn && s.security.roles.includes(globalRoles.CLIENT);
export const selectRoles = (s: RootState) => s.security.roles;
export const selectToken = (s: RootState) => s.security.token;
// no selectLockoutUntil: lockout not tracked in frontend state

// ==== thunks (typed) ====
// NOTE: Retries/lockout live here. Component just calls securityLogin(...)
export const doSecurityLogin = createAsyncThunk<
  { roles: string[]; token: Token; userSession: UserSessionData | null },
  { request: LoginRequest },
  { state: RootState; rejectValue: string }
>(securityLogin, async ({ request }, { rejectWithValue, dispatch }) => {
  // Attempt login via repo; backend handles lockout attempts and duration
  const result = (await new AuthRepository().login(
    request
  )) as LoginResult | null;
  if (!result) return rejectWithValue("Login failed");

  // Backend-side lockout signal (optional & additive)
  if (result.isLockedOut) {
    const untilISO = result.lockoutEndDate ?? null;
    if (untilISO) {
      const minutesLeft = getMinutesUntil(untilISO);
      if (minutesLeft > 0) {
        return rejectWithValue(
          `Cuenta bloqueada. Intente en ${minutesLeft} minuto(s).`
        );
      }
    }
    return rejectWithValue("Su cuenta está bloqueada. Intente más tarde.");
  }

  // Successful login → fetch roles
  if (result.successLogin || result.accessToken) {
    const token: Token = { ...result };
    dispatch(setToken(token));
    const roles =
      (await new RolesRepository().getListAllAuthenticated())?.map(
        (r) => r.name
      ) ?? [];
    const userSession: UserSessionData | null = { ...(result ?? "") };

    return {
      roles,
      token,
      userSession,
    };
  }

  // Failed login (no success flag). Backend governs attempts/lockout
  // Prefer backend-provided message when available
  if (result.failedLoginMessage) {
    return rejectWithValue(result.failedLoginMessage);
  }

  return rejectWithValue("Credenciales incorrectas.");
});

export const doSecurityLogout = createAsyncThunk<
  boolean,
  void,
  { state: RootState }
>(securityLogout, async () => true);

export const doSecurityRefreshToken = createAsyncThunk<
  Token,
  void,
  { state: RootState }
>(securityRefreshToken, async (_arg, { getState }) => {
  const token = getState().security.token;
  if (!token?.accessToken)
    throw new Error("No refresh token available. Please log in.");
  const { default: config } = await import("../config");
  try {
    const response = await axios.post(
      `${config.apiUrl}authentication/refresh-token`,
      {
        accessToken: token.accessToken,
      }
    );
    return {
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    } as Token;
  } catch (err) {
    // Let infra emit sessionExpired and index.ts handle redirect
    throw new Error("Token refresh failed");
  }
});

export const doSecurityFetchRoles = createAsyncThunk<
  string[],
  void,
  { state: RootState }
>(securityFetchRoles, async (_arg, { getState }) => {
  const s = getState();
  if (!s.security.isLoggedIn) return [];
  return (
    (await new RolesRepository().getListAllAuthenticated())?.map(
      (r) => r.name
    ) ?? []
  );
});

export const doSecurityRegisterClient = createAsyncThunk<
  unknown,
  RegisterClient,
  { state: RootState }
>(securityRegisterClient, async (request) =>
  new AuthRepository().register(request)
);

export const doSecurityRequestPasswordReset = createAsyncThunk<
  boolean,
  PasswordResetRequest,
  { state: RootState }
>(
  securityRequestPasswordReset,
  async (request) =>
    !!(await new AuthRepository().requestPasswordReset(request))
);

export const doSecurityPasswordReset = createAsyncThunk<
  boolean,
  PasswordReset,
  { state: RootState }
>(
  securityPasswordReset,
  async (request) => !!(await new AuthRepository().passwordReset(request))
);

export const selectHasRole = (role: string) => (s: RootState) =>
  s.security.roles.includes(role);

// ==== slice ====
const securitySlice = createSlice({
  name: "security",
  initialState: initialSecurityState,
  reducers: {
    setToken(state, a: PayloadAction<Token | undefined>) {
      state.token = a.payload ?? undefined;
      state.isLoggedIn = !!a.payload;
    },
    setUserSession(state, a: PayloadAction<UserSessionData | undefined>) {
      state.userSession = a.payload ?? undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // login
      .addCase(doSecurityLogin.pending, (s) => {
        s.storeStatus = StoreStatus.loading;
        s.error = null;
      })
      .addCase(doSecurityLogin.fulfilled, (s, a) => {
        s.storeStatus = StoreStatus.succeeded;
        s.isLoggedIn = true;
        s.roles = a.payload.roles;
        s.token = a.payload.token;
        s.userSession = a.payload?.userSession ?? undefined;
      })
      .addCase(doSecurityLogin.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.isLoggedIn = false;
        s.token = undefined;
        s.roles = [];
        s.userSession = undefined;
        s.error = (a.payload as string) ?? a.error?.message ?? "Login failed";
      })

      // logout
      .addCase(doSecurityLogout.fulfilled, (s) => {
        s.isLoggedIn = false;
        s.roles = [];
        s.token = undefined;
        s.userSession = undefined;
        s.storeStatus = StoreStatus.idle;
        s.error = null;
      })

      // refresh
      .addCase(doSecurityRefreshToken.pending, (s) => {
        s.storeStatus = StoreStatus.loading;
      })
      .addCase(doSecurityRefreshToken.fulfilled, (s, a) => {
        s.token = a.payload;
        s.storeStatus = StoreStatus.succeeded;
      })
      .addCase(doSecurityRefreshToken.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Token refresh failed");
        s.isLoggedIn = false;
        s.roles = [];
        s.token = undefined;
        s.userSession = undefined;
      })

      // roles
      .addCase(doSecurityFetchRoles.pending, (s) => {
        s.storeStatus = StoreStatus.loading;
      })
      .addCase(doSecurityFetchRoles.fulfilled, (s, a) => {
        s.storeStatus = StoreStatus.succeeded;
        s.roles = a.payload;
      })
      .addCase(doSecurityFetchRoles.rejected, (s, a) => {
        s.storeStatus = StoreStatus.failed;
        s.error = String(a.error.message ?? "Fetch roles failed");
      });
  },
});

export const { setUserSession, setToken } = securitySlice.actions;
export default securitySlice.reducer;
export const securityReducer = securitySlice.reducer;

// ==== facade hook ====
export function useSecurityStore() {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector(selectSecurity);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const roles = useSelector(selectRoles);
  const isLoggedInClient = isLoggedIn && roles.includes(globalRoles.CLIENT);

  const securityLoginAction = useCallback(
    (request: LoginRequest) => dispatch(doSecurityLogin({ request })),
    [dispatch]
  );
  const securityLogoutAction = useCallback(
    () => dispatch(doSecurityLogout()),
    [dispatch]
  );
  const securityFetchRolesAction = useCallback(
    () => dispatch(doSecurityFetchRoles()).unwrap(),
    [dispatch]
  );
  const securityRefreshTokenAction = useCallback(
    () => dispatch(doSecurityRefreshToken()).unwrap(),
    [dispatch]
  );

  return {
    ...state,
    isLoggedIn,
    isLoggedInClient,
    roles,
    hasRole: (r: string) => roles.includes(r),
    securityLogin: securityLoginAction,
    securityLogout: securityLogoutAction,
    securityFetchRoles: securityFetchRolesAction,
    securityRefreshToken: securityRefreshTokenAction,
  } as const;
}
